import React, { useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { clearAppCache, resolveImageUrl } from "../../utils";

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface RoleData {
  RoleId: number;
  RoleName: string;
  roleMenuDatas: null;
}

interface UserRoleData {
  UserRoleId: number;
  UserId: number;
  MallId: number;
  MallName: string;
  TenantId?: number;
  roleData: RoleData;
}

interface UserRoleResponse {
  userRoleDatas: UserRoleData[];
  Code: number;
  Message: string;
}

declare const API_BASE_URL_TOKEN: string;
declare const API_BASE_URL: string;
declare const API_BASE_URL_TOKEN_DEV: string;
declare const API_BASE_URL_DEV: string;

export default function TenantAccessLogin(): React.JSX.Element {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [userRoles, setUserRoles] = useState<UserRoleData[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const navigate = useNavigate();

  React.useEffect(() => {
    const rememberMe = localStorage.getItem("remember_me") === "true";
    if (rememberMe) {
      const savedEmail = localStorage.getItem("user_email") || "";
      const savedPassword = localStorage.getItem("user_password") || "";
      setFormData({
        email: savedEmail,
        password: savedPassword,
        rememberMe: true,
      });
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError("");
  };

  const fetchUserRole = async (
    email: string,
    password: string
  ): Promise<UserRoleResponse> => {
    const url = `${API_BASE_URL}/user/v1/getuserrole`;
    try {
      const response = await axios({
        url,
        method: "GET",
        timeout: 0,
        headers: { "X-Username": email, "X-Password": password },
      });
      if (response.status === 200) return response.data;
      throw new Error("Failed to fetch user roles.");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.Message || "Failed to fetch user roles."
        );
      }
      throw error instanceof Error ? error : new Error("Failed to fetch user roles.");
    }
  };

  // --- Fetch token dari satu base URL -----------------------------------------
  const fetchToken = async (
    baseTokenUrl: string,
    email: string,
    password: string,
    userroleid: string
  ): Promise<{ data: { access_token: string } | null; error: any }> => {
    const url = `${baseTokenUrl}/token`;
    const body = new URLSearchParams({
      grant_type: "password",
      username: email,
      password: password,
      userroleid: userroleid,
    });
    try {
      const response = await axios.post(url, body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const saveToStorage = (
    storage: Storage,
    selectedUserRole: UserRoleData,
    email: string,
    password: string,
    prodToken: string | null,
    devToken: string | null
  ) => {
    const tenantId =
      selectedUserRole.TenantId != null && selectedUserRole.TenantId !== 0
        ? selectedUserRole.TenantId.toString()
        : (selectedUserRole.roleData?.RoleName?.toUpperCase() === "HO" ? "0" : null);
    const mallId = selectedUserRole.MallId.toString();
    const roleName = selectedUserRole.roleData.RoleName;

    if (prodToken) {
      storage.setItem("access_token", prodToken);
      storage.setItem("token", prodToken);
    }

    if (devToken) {
      storage.setItem("access_token_dev", devToken);
      storage.setItem("token_dev", devToken);
    }

    storage.setItem("user_email", email);
    storage.setItem("user_password", password);
    storage.setItem("mall_id", mallId);
    storage.setItem("role_name", roleName);
    const loginTenantIdVal =
      selectedUserRole.TenantId != null && selectedUserRole.TenantId !== 0
        ? selectedUserRole.TenantId.toString()
        : "0";
    storage.setItem("login_tenant_id", loginTenantIdVal);

    if (tenantId !== null) {
      storage.setItem("tenant_id", tenantId);
    } else {
      storage.removeItem("tenant_id");
    }

    if (formData.rememberMe) {
      localStorage.setItem("remember_me", "true");
      localStorage.setItem("user_email", email);
      localStorage.setItem("user_password", password);
    } else {
      localStorage.removeItem("remember_me");
      localStorage.removeItem("user_email");
      localStorage.removeItem("user_password");
    }
  };

  // --- Proses login setelah role dipilih ---------------------------------------
  const proceedLogin = async (selectedUserRole: UserRoleData) => {
    try {
      const userRoleId = selectedUserRole.UserRoleId.toString();

      // Fetch token dari prod dan dev secara paralel
      const [prodResult, devResult] = await Promise.all([
        fetchToken(API_BASE_URL_TOKEN, formData.email, formData.password, userRoleId),
        fetchToken(API_BASE_URL_TOKEN_DEV, formData.email, formData.password, userRoleId),
      ]);

      const prodToken = prodResult.data?.access_token ?? null;
      const devToken = devResult.data?.access_token ?? null;

      const isProductionEnv =
        window.location.hostname === "tenant.lippomallpuri.com" ||
        window.location.hostname === "tenantaccess.lippomalls.com" ||
        API_BASE_URL.includes("tenantaccessapi");

      if (isProductionEnv && !prodToken) {
        const err = prodResult.error;
        let errMsg = "Gagal menyambungkan ke server autentikasi produksi.";
        if (axios.isAxiosError(err)) {
          errMsg = err.response?.data?.error_description || err.response?.data?.error || err.message;
        } else if (err instanceof Error) {
          errMsg = err.message;
        }
        throw new Error(`Login Produksi Gagal: ${errMsg}`);
      }

      if (!prodToken && !devToken) {
        const err = devResult.error || prodResult.error;
        let errMsg = "Gagal mendapatkan token dari server.";
        if (axios.isAxiosError(err)) {
          errMsg = err.response?.data?.error_description || err.response?.data?.error || err.message;
        } else if (err instanceof Error) {
          errMsg = err.message;
        }
        throw new Error(`Login Gagal: ${errMsg}`);
      }

      clearAppCache();
      const storage = formData.rememberMe ? localStorage : sessionStorage;
      saveToStorage(storage, selectedUserRole, formData.email, formData.password, prodToken, devToken);

      await Swal.fire({
        title: "Login Successful!",
        text: "Redirecting to dashboard...",
        icon: "success",
        showConfirmButton: false,
        timer: 1200,
        timerProgressBar: true,
        allowOutsideClick: false,
      });
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError("");
    try {
      if (!formData.email || !formData.password) {
        throw new Error("Email and password fields must be filled");
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error("Please enter a valid email address");
      }

      const userRole = await fetchUserRole(formData.email, formData.password);

      if (userRole.Code === 6) {
        setError(userRole.Message);
        setIsLoading(false);
        return;
      }
      if (userRole.Code === 7) {
        await Swal.fire({
          title: "User Inactive",
          text: "Redirecting to activation form...",
          icon: "warning",
          showConfirmButton: false,
          timer: 1200,
          timerProgressBar: true,
          allowOutsideClick: false,
        });
        navigate("/Inactivate");
        setIsLoading(false);
        return;
      }

      if (userRole.userRoleDatas && userRole.userRoleDatas.length > 1) {
        setUserRoles(userRole.userRoleDatas);
        setShowRoleModal(true);
        setIsLoading(false);
      } else {
        await proceedLogin(userRole.userRoleDatas[0]);
      }
    } catch (err) {
      let message = "An unexpected error occurred during login";
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const serverMsg =
          err.response?.data?.message || err.response?.data?.error || "";
        message = status
          ? `Server error (${status}): ${serverMsg || "Unknown error"}`
          : `Network error: ${err.message}`;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
      setIsLoading(false);
    }
  };

  const handleRoleSelection = async () => {
    if (!selectedRoleId) {
      setError("Please select a role to continue");
      return;
    }
    setIsLoading(true);
    setShowRoleModal(false);
    try {
      const selectedRole = userRoles.find(
        (role) => role.UserRoleId.toString() === selectedRoleId
      );
      if (selectedRole) await proceedLogin(selectedRole);
    } catch (err) {
      let message = "An unexpected error occurred during login";
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const serverMsg =
          err.response?.data?.message || err.response?.data?.error || "";
        message = status
          ? `Server error (${status}): ${serverMsg || "Unknown error"}`
          : `Network error: ${err.message}`;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoading) handleLogin();
  };

  const handleCancelRoleSelection = () => {
    setShowRoleModal(false);
    setSelectedRoleId("");
    setUserRoles([]);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* --- Top logo bar --- */}
      <div className="flex-shrink-0 flex justify-center pt-5 pb-2 px-4">
        <img
          src={resolveImageUrl("/images/logo_lippo.png")}
          alt="Lippo Malls Logo"
          className="h-7 w-auto object-contain"
        />
      </div>

      {/* --- Role modal --- */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Select Role</h2>
              <button
                onClick={handleCancelRoleSelection}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                aria-label="Close modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Select your role
              </label>
              <div className="relative">
                <select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                >
                  <option value="">-- Choose Role --</option>
                  {userRoles.map((role) => (
                    <option key={role.UserRoleId} value={role.UserRoleId}>
                      {role.roleData.RoleName} - {role.MallName}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <div className="space-y-2 pt-2">
                <button
                  onClick={handleRoleSelection}
                  disabled={!selectedRoleId || isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold py-2.5 px-4 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                >
                  {isLoading ? "Loading..." : "Continue"}
                </button>
                <button
                  onClick={handleCancelRoleSelection}
                  disabled={isLoading}
                  className="w-full bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 font-semibold py-2.5 px-4 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Main content --- */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left illustration - desktop only */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-end overflow-hidden">
          <div className="w-full max-w-4xl scale-110 origin-right translate-x-4 xl:translate-x-8">
            <img
              src={resolveImageUrl("/images/Front Image Tenant Access.png")}
              alt="Login Illustration"
              className="w-full h-auto object-contain mix-blend-multiply"
            />
          </div>
        </div>

        {/* Right - form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center lg:justify-start px-4 sm:px-8 lg:px-12 py-6 lg:py-0">
          <div className="w-full max-w-sm sm:max-w-md mx-auto">
            {/* Mobile illustration */}
            <div className="lg:hidden text-center mb-4">
              <img
                src={resolveImageUrl("/images/Front Image Tenant Access.png")}
                alt="Login Illustration"
                className="w-48 sm:w-64 h-auto mx-auto object-contain"
              />
            </div>

            {/* Card */}
            <div className="bg-white rounded-2xl px-6 sm:px-8 py-7 shadow-2xl border border-gray-200">
              {/* Logo + subtitle */}
              <div className="text-center mb-6 flex flex-col items-center">
                <img
                  src={resolveImageUrl("/images/logo_TA.png")}
                  alt="Tenant Access Logo"
                  className="h-20 sm:h-24 w-auto mb-2 object-contain"
                />
                <p className="text-sm text-gray-500 font-medium">
                  Sign in to manage your Brand
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs sm:text-sm break-words">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    placeholder="Enter your email"
                    disabled={isLoading}
                    autoComplete="username"
                  />
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200 pr-11"
                      placeholder="Enter your password"
                      disabled={isLoading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember me */}
                <div className="flex items-center gap-2">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor="rememberMe"
                    className="text-sm text-gray-600 cursor-pointer select-none"
                  >
                    Remember me
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Signing you in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* --- Footer --- */}
      <div className="flex-shrink-0 py-4 text-center px-4">
        <p className="text-xs sm:text-sm text-gray-500">
          {"\u00A9"} 2026 Lippo Malls Indonesia
        </p>
      </div>
    </div>
  );
}